import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, Download, FileSpreadsheet, FileUp, Loader2, XCircle, AlertTriangle,
} from "lucide-react";
import { API, api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatDateID, formatRupiah } from "../lib/format";
import { tdClass, thClass } from "../components/Common";

const FIELDS = [
  ["date", "Tanggal", true],
  ["number", "No Bukti", false],
  ["description", "Uraian", true],
  ["category", "Kategori", false],
  ["type", "Jenis (Pemasukan/Pengeluaran)", false],
  ["party", "Sumber/Penerima", false],
  ["income", "Pemasukan (nominal)", false],
  ["expense", "Pengeluaran (nominal)", false],
  ["amount", "Nominal (kolom tunggal)", false],
  ["notes", "Keterangan", false],
];

const STEPS = ["Upload", "Preview", "Mapping", "Validasi", "Hasil"];

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="import-stepper">
      {STEPS.map((s, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <div key={s} className="flex items-center gap-1">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              active ? "bg-slate-900 text-white" : done ? "bg-[#0A3A22] text-emerald-400" : "bg-slate-800 text-slate-400"
            }`}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="font-num">{n}</span>}
              {s}
            </div>
            {n < STEPS.length && <div className="w-4 h-px bg-slate-300" />}
          </div>
        );
      })}
    </div>
  );
}

export default function ImportExcel() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canResmi = hasPerm("create_income") || hasPerm("create_expense");
  const canImf = hasPerm("create_income_imf") || hasPerm("create_expense_imf");
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defaultType, setDefaultType] = useState("expense");
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [dupMode, setDupMode] = useState("skip_duplicates");
  const [book, setBook] = useState(canImf && !canResmi ? "imf" : "resmi");
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBatch(data);
      const m = {};
      Object.entries(data.suggested_mapping).forEach(([k, v]) => (m[k] = String(v)));
      setMapping(m);
      setStep(2);
      toast.success(`File "${data.filename}" terbaca: ${data.total_rows} baris`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const validate = async () => {
    setValidating(true);
    try {
      const payload = { import_id: batch.import_id, mapping: {}, default_type: defaultType, expense_columns: batch.expense_columns || [] };
      Object.entries(mapping).forEach(([k, v]) => (payload.mapping[k] = parseInt(v, 10)));
      const { data } = await api.post("/import/validate", payload);
      setValidation(data);
      setStep(4);
      if (data.errors > 0) toast.warning(`${data.errors} baris bermasalah ditemukan`);
      else toast.success(`${data.valid} baris valid, siap diimport`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setValidating(false);
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/import/commit", {
        import_id: batch.import_id,
        mode: dupMode,
        book,
      });
      setResult(data);
      setStep(5);
      toast.success(`${data.imported} transaksi berhasil diimport`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCommitting(false);
    }
  };

  const reset = () => {
    setStep(1); setBatch(null); setMapping({}); setValidation(null); setResult(null);
  };

  const showDefaultType =
    batch &&
    mapping.amount !== undefined &&
    parseInt(mapping.amount, 10) >= 0 &&
    parseInt(mapping.income ?? -1, 10) < 0 &&
    parseInt(mapping.expense ?? -1, 10) < 0 &&
    parseInt(mapping.type ?? -1, 10) < 0;

  return (
    <div className="space-y-5" data-testid="import-excel-wizard">
      <Stepper step={step} />

      {step === 1 && (
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-8">
          <div className="max-w-xl mx-auto text-center space-y-4">
            <div className="p-4 rounded-full bg-emerald-500/15 inline-flex">
              <FileSpreadsheet className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-slate-100">Upload File Excel</h2>
            <p className="text-sm text-slate-400">
              Unggah file .xlsx, .xls, atau .csv berisi data transaksi kas. Sistem akan membaca kolom
              secara otomatis dan Anda dapat menyesuaikan mapping sebelum data disimpan.
            </p>
            <a
              data-testid="btn-download-template"
              href={`${API}/import/template`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:underline"
            >
              <Download className="w-4 h-4" /> Download Template Excel
            </a>
            <div
              className="border-2 border-dashed border-slate-700 rounded-xl p-8 hover:border-slate-700 transition-colors cursor-pointer"
              onClick={() => fileRef.current?.click()}
              data-testid="upload-dropzone"
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-2 text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <p className="text-sm">Membaca file…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-slate-400">
                  <FileUp className="w-8 h-8" />
                  <p className="text-sm font-medium">Klik untuk memilih file</p>
                  <p className="text-xs">.xlsx / .xls / .csv — maksimal 10 MB</p>
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              data-testid="input-file-excel"
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </div>
        </div>
      )}

      {step === 2 && batch && (
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 pt-5 pb-3 flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-100">Preview Data — {batch.filename}</h3>
              <p className="text-xs text-slate-400">
                {batch.total_rows} baris data terdeteksi{batch.sheets_used > 1 ? ` dari ${batch.sheets_used} sheet` : ""}. Menampilkan 10 baris pertama.
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm text-left">
              <thead>
                <tr>
                  <th className={thClass}>#</th>
                  {batch.columns.map((c, i) => <th key={i} className={thClass}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {batch.preview.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-800/40">
                    <td className={`${tdClass} font-num text-xs text-slate-400`}>{i + 2}</td>
                    {batch.columns.map((_, j) => (
                      <td key={j} className={`${tdClass} max-w-[200px]`}>
                        <span className="line-clamp-1">{row[j] ?? ""}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between px-5 py-4 border-t border-slate-800/60">
            <button data-testid="btn-import-back-step2" onClick={reset} className="border border-slate-700 bg-[#1E293B] text-slate-200 hover:bg-slate-800/40 font-medium px-4 py-2 rounded-lg text-sm">Kembali</button>
            <button data-testid="btn-import-next" onClick={() => setStep(3)} className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-4 py-2 rounded-lg text-sm">
              Lanjut ke Mapping Kolom
            </button>
          </div>
        </div>
      )}

      {step === 3 && batch && (
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-6 space-y-5">
          <div>
            <h3 className="text-base font-bold text-slate-100">Mapping Kolom</h3>
            <p className="text-xs text-slate-400">
              Cocokkan kolom file Excel dengan kolom sistem. Kolom bertanda * wajib dipetakan.
              Jika file memiliki kolom Pemasukan & Pengeluaran terpisah, jenis transaksi ditentukan otomatis.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map(([key, label, required]) => (
              <div key={key} className="space-y-1.5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  {label} {required && <span className="text-rose-500">*</span>}
                </p>
                <select
                  data-testid={`select-map-${key}`}
                  className="w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  value={mapping[key] ?? "-1"}
                  onChange={(e) => setMapping((m) => ({ ...m, [key]: e.target.value }))}
                >
                  <option value="-1">— Tidak dipetakan —</option>
                  {batch.columns.map((c, i) => (
                    <option key={i} value={String(i)}>{c}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          {batch.expense_column_names?.length > 0 && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/15 p-4" data-testid="expense-columns-info">
              <p className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Kolom kategori pengeluaran terdeteksi otomatis
              </p>
              <p className="text-xs text-emerald-400 mt-1">
                Setiap nominal pada kolom berikut akan menjadi transaksi pengeluaran dengan kategori sesuai nama kolom:
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {batch.expense_column_names.map((n) => (
                  <span key={n} className="bg-[#1E293B] border border-emerald-500/30 text-emerald-400 rounded-full px-2.5 py-0.5 text-xs font-semibold">
                    {n}
                  </span>
                ))}
              </div>
            </div>
          )}
          {showDefaultType && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
              <p className="text-sm font-semibold text-amber-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> File hanya memiliki kolom Nominal
              </p>
              <p className="text-xs text-amber-300 mt-1">Pilih jenis transaksi untuk semua baris:</p>
              <select
                data-testid="select-default-type"
                className="mt-2 rounded-lg border border-amber-500/30 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                value={defaultType}
                onChange={(e) => setDefaultType(e.target.value)}
              >
                <option value="income">Semua sebagai Pemasukan</option>
                <option value="expense">Semua sebagai Pengeluaran</option>
              </select>
            </div>
          )}
          <div className="flex justify-between pt-2">
            <button data-testid="btn-import-back-step3" onClick={() => setStep(2)} className="border border-slate-700 bg-[#1E293B] text-slate-200 hover:bg-slate-800/40 font-medium px-4 py-2 rounded-lg text-sm">Kembali</button>
            <button
              data-testid="btn-validate-import"
              onClick={validate}
              disabled={validating}
              className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2"
            >
              {validating && <Loader2 className="w-4 h-4 animate-spin" />}
              Validasi Data
            </button>
          </div>
        </div>
      )}

      {step === 4 && validation && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ["Total Baris", validation.total_rows, "text-slate-100", "stat-total-rows"],
              ["Data Valid", validation.valid, "text-emerald-400", "stat-valid-rows"],
              ["Data Error", validation.errors, "text-rose-400", "stat-error-rows"],
              ["Data Duplikat", validation.duplicates, "text-amber-300", "stat-duplicate-rows"],
            ].map(([label, val, cls, tid]) => (
              <div key={tid} data-testid={tid} className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-5">
                <p className="text-xs font-semibold tracking-wider text-slate-400 uppercase">{label}</p>
                <p className={`font-num text-2xl font-bold mt-2 ${cls}`}>{val}</p>
              </div>
            ))}
          </div>

          {validation.skipped > 0 && (
            <p className="text-xs text-slate-400 px-1" data-testid="import-skipped-note">
              {validation.skipped} baris dilewati otomatis (baris kosong atau baris saldo awal seperti "Available Balance").
            </p>
          )}

          {validation.error_details.length > 0 && (
            <div className="bg-[#1E293B] border border-rose-500/30 rounded-xl shadow-sm overflow-hidden">
              <div className="px-5 pt-4 pb-2 flex items-center gap-2">
                <XCircle className="w-4 h-4 text-rose-500" />
                <h3 className="text-sm font-bold text-slate-100">Detail Error ({validation.errors} baris)</h3>
              </div>
              <div className="max-h-48 overflow-y-auto px-5 pb-4">
                {validation.error_details.map((e) => (
                  <p key={e.row_no} className="text-xs text-rose-400 py-1 border-b border-rose-50 last:border-0" data-testid={`error-row-${e.row_no}`}>
                    Baris {e.row_no}: {e.messages.join(", ")}
                  </p>
                ))}
              </div>
            </div>
          )}

          {validation.duplicates > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-2" data-testid="duplicate-options">
              <p className="text-sm font-semibold text-amber-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {validation.duplicates} data duplikat terdeteksi (No Bukti + Tanggal + Nominal + Uraian sama)
              </p>
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input type="radio" data-testid="radio-skip-duplicates" checked={dupMode === "skip_duplicates"} onChange={() => setDupMode("skip_duplicates")} />
                Lewati duplikat — hanya import data baru
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input type="radio" data-testid="radio-import-all" checked={dupMode === "import_all"} onChange={() => setDupMode("import_all")} />
                Import tetap — sertakan data duplikat
              </label>
            </div>
          )}

          {validation.valid_preview.length > 0 && (
            <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
              <div className="px-5 pt-4 pb-2">
                <h3 className="text-sm font-bold text-slate-100">Pratinjau Data Valid</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm text-left">
                  <thead>
                    <tr>
                      <th className={thClass}>Baris</th>
                      <th className={thClass}>Tanggal</th>
                      <th className={thClass}>Jenis</th>
                      <th className={thClass}>Uraian</th>
                      <th className={thClass}>Kategori</th>
                      <th className={`${thClass} text-right`}>Nominal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validation.valid_preview.map((r) => (
                      <tr key={r.row_no} className={r.duplicate ? "bg-amber-500/10/60" : "hover:bg-slate-800/40"}>
                        <td className={`${tdClass} font-num text-xs text-slate-400`}>{r.row_no}</td>
                        <td className={tdClass}>{formatDateID(r.date)}</td>
                        <td className={tdClass}>
                          <span className={r.type === "income" ? "text-emerald-400 font-semibold text-xs" : "text-rose-400 font-semibold text-xs"}>
                            {r.type === "income" ? "PEMASUKAN" : "PENGELUARAN"}
                          </span>
                          {r.duplicate && <span className="ml-2 text-[10px] font-bold text-amber-600 uppercase">Duplikat</span>}
                        </td>
                        <td className={`${tdClass} max-w-[240px]`}><span className="line-clamp-1">{r.description}</span></td>
                        <td className={tdClass}>{r.category}</td>
                        <td className={`${tdClass} text-right font-num`}>{formatRupiah(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-5 mb-4" data-testid="import-book-section">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Masukkan ke Kas</p>
            <div className="flex gap-2" data-testid="import-book-selector">
              {canResmi && (
                <button
                  type="button"
                  data-testid="import-book-resmi"
                  onClick={() => setBook("resmi")}
                  className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                    book === "resmi"
                      ? "bg-slate-900 text-cyan-400 border-cyan-400"
                      : "border-slate-800 text-slate-400 hover:text-slate-100 hover:border-slate-700"
                  }`}
                >
                  Kas Resmi
                </button>
              )}
              {canImf && (
                <button
                  type="button"
                  data-testid="import-book-imf"
                  onClick={() => setBook("imf")}
                  className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                    book === "imf"
                      ? "bg-slate-900 text-cyan-400 border-cyan-400"
                      : "border-slate-800 text-slate-400 hover:text-slate-100 hover:border-slate-700"
                  }`}
                >
                  Kas IMF
                </button>
              )}
            </div>
          </div>

          <div className="flex justify-between">
            <button data-testid="btn-import-back-step4" onClick={() => setStep(3)} className="border border-slate-700 bg-[#1E293B] text-slate-200 hover:bg-slate-800/40 font-medium px-4 py-2 rounded-lg text-sm">Kembali</button>
            <div className="flex gap-2">
              <button data-testid="btn-import-cancel" onClick={reset} className="border border-slate-700 bg-[#1E293B] text-slate-200 hover:bg-slate-800/40 font-medium px-4 py-2 rounded-lg text-sm">Batalkan Import</button>
              <button
                data-testid="btn-import-commit"
                onClick={commit}
                disabled={committing || validation.valid === 0 || (validation.duplicates === validation.valid && dupMode === "skip_duplicates")}
                className="bg-emerald-600 text-white hover:bg-emerald-700 font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {committing && <Loader2 className="w-4 h-4 animate-spin" />}
                Import {dupMode === "skip_duplicates" ? validation.valid - validation.duplicates : validation.valid} Data
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 5 && result && (
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-10 text-center space-y-4 max-w-xl mx-auto">
          <div className="p-4 rounded-full bg-emerald-500/15 inline-flex">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <h2 className="text-xl font-bold text-slate-100">Import Selesai</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
            <div className="rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-4">
              <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Berhasil Diimport</p>
              <p className="font-num text-2xl font-bold text-emerald-800 mt-1" data-testid="import-result-imported">{result.imported}</p>
            </div>
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-4">
              <p className="text-xs font-semibold text-amber-300 uppercase tracking-wider">Duplikat Dilewati</p>
              <p className="font-num text-2xl font-bold text-amber-300 mt-1" data-testid="import-result-skipped">{result.skipped_duplicates}</p>
            </div>
          </div>
          <div className="flex justify-center gap-2 pt-2">
            <button onClick={reset} className="border border-slate-700 bg-[#1E293B] text-slate-200 hover:bg-slate-800/40 font-medium px-4 py-2 rounded-lg text-sm">Import File Lain</button>
            <button data-testid="btn-goto-transactions" onClick={() => navigate("/transaksi")} className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-4 py-2 rounded-lg text-sm">
              Lihat Semua Transaksi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
